import { useEffect, useState } from "react";
import { HiArrowSmLeft } from "react-icons/hi";
import { httpService } from "../../../../../src/services/httpService";
import {
    FiPhone,
    FiUser,
    FiChevronDown,
    FiChevronUp,
} from "react-icons/fi";
import { MdHistory, MdLocalHotel, MdShoppingCart, MdPets } from "react-icons/md";
import { FaRuler } from "react-icons/fa";
import AddAppointment from "../appointmentcalendar/AddAppointment";

const BoardingHistoryDrawer = ({ isOpen = false, onClose = () => { }, clientId = null, clientData = null, onReorder = null }) => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedBooking, setExpandedBooking] = useState(null);
    const [showAddAppointment, setShowAddAppointment] = useState(false);
    const [selectedBookingForReorder, setSelectedBookingForReorder] = useState(null);

    useEffect(() => {
        if (isOpen && clientId) {
            fetchBoardingHistory();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, clientId]);

    const fetchBoardingHistory = async () => {
        setLoading(true);
        try {
            const res = await httpService.get(`/vendorbooking/bookingHistory/${clientId}`);
            // Transform the new response structure to flatten orders with their items
            const transformedBookings = [];
            if (res.data && Array.isArray(res.data)) {
                res.data.forEach(order => {
                    if (order.order_items && Array.isArray(order.order_items)) {
                        order.order_items.forEach(item => {
                            transformedBookings.push({
                                // Order level data
                                order_id: order._id,
                                order_number: order.order_number,
                                order_type: order.order_type,
                                total_amount: order.total_amount,
                                total_paid_amount: order.total_paid_amount,
                                payment_status: order.payment_status,
                                createdAt: order.createdAt,
                                vendor_client: order.vendor_client_id,

                                // Item level data (mapped to existing field names)
                                ref_id: item.ref_id || item._id,
                                _id: item._id,
                                type: item.item_type,
                                start_date: item.start_date,
                                end_date: item.end_date,
                                pet_name: item.pet_name,
                                status: item.status,
                                quantity: item.quantity,
                                sku_number: item.sku_number,

                                // Legacy compatibility
                                total_price: item.total_price,
                                service_name: item.service_name || 'Service',
                                service_type_name: item.service_type_name || '',
                                room_name: item.room_name || '',
                                room_number: item.room_number || '',
                                pet_size: item.pet_size || '',
                                pet_breed: item.pet_breed || '',

                                // Additional fields from new structure
                                unit_price: item.unit_price,
                                discount: item.discount,
                                gst: item.gst,
                                duration: item.duration,
                                start_time: item.start_time,
                                addons: item.addons || [],
                                service_id: item.service_id,
                                service_category_type_id: item.service_category_type_id
                            });
                        });
                    }
                });
            }
            setBookings(transformedBookings);
        } catch (error) {
            console.error("Error fetching boarding history:", error);
            setBookings([]);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const s = (status || "").toLowerCase();
        if (s === "completed") return "bg-emerald-200 text-emerald-800";
        if (s === "confirmed") return "bg-green-200 text-green-800";
        if (s === "booked") return "bg-blue-200 text-blue-800";
        if (s === "cancel" || s === "cancelled") return "bg-red-200 text-red-800";
        if (s === "no show" || s === "no_show") return "bg-purple-200 text-purple-800";
        if (s === "draft") return "bg-gray-200 text-gray-800";
        if (s === "arrived") return "bg-indigo-200 text-indigo-800";
        if (s === "partial") return "bg-yellow-200 text-yellow-800";
        return "bg-gray-200 text-gray-800";
    };

    const formatDate = (d) => {
        if (!d) return "";
        try {
            return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
        } catch (_) {
            return d;
        }
    };

    const handleReorder = (booking) => {
        // Determine the type based on item_type
        let appointmentType = "Appointment"; // default

        if (booking.type === 'boarding' || booking.item_type === 'boarding' ||
            booking.type === 'day_care' || booking.item_type === 'day_care' ||
            booking.type === 'daycare' || booking.item_type === 'daycare') {
            appointmentType = "Boarding";
        } else if (booking.type === 'dog_walking' || booking.item_type === 'dog_walking' ||
            booking.type === 'vet_clinic' || booking.item_type === 'vet_clinic' ||
            booking.type === 'grooming' || booking.item_type === 'grooming') {
            appointmentType = "Appointment";
        }

        setSelectedBookingForReorder({
            ...booking,
            type: appointmentType,
            // Ensure all necessary fields are present for AddAppointment
            service_id: booking.service_id,
            vendor_pet_id: booking.vendor_pet_id || booking.pet_id,
            pet_id: booking.vendor_pet_id || booking.pet_id,
            service_type_name: booking.service_type_name || booking.service_name || 'Service',
            service_category_type_id: booking.service_category_type_id || booking.service_type_id,
            service_type_id: booking.service_type_id || booking.service_category_type_id,
            item_type: booking.type || booking.item_type,
            // Include client data for AddAppointment
            vendor_client: clientData,
            client: clientData,
        });
        setShowAddAppointment(true);
    };

    const handleAddAppointmentClose = () => {
        setShowAddAppointment(false);
        setSelectedBookingForReorder(null);
    };

    const handleAddAppointmentSave = (data) => {
        // Handle the saved appointment data if needed
        console.log('Appointment saved from reorder:', data);
        setShowAddAppointment(false);
        setSelectedBookingForReorder(null);
    };

    return (
        <div
            className={`fixed inset-0 z-50 transition-all duration-300 ease-in-out ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
        >
            <div className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-[2px]" onClick={onClose} />
            <div
                className={`absolute right-0 top-0 h-full w-full lg:w-2/5 bg-white shadow-xl transform transition-transform duration-300 ease-out ${isOpen ? "translate-x-0" : "translate-x-full"
                    }`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-start items-center p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm gap-3">
                    <button
                        onClick={onClose}
                        className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/70 transition-colors duration-200"
                    >
                        <HiArrowSmLeft className="w-6 h-6 text-gray-700" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center">
                            <MdLocalHotel className="w-4 h-4 text-white" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800">Boarding History</h2>
                    </div>
                </div>

                <div className="flex h-[calc(100vh-73px)] flex-col">
                    <div className="p-6 space-y-6 overflow-auto bg-gradient-to-b from-gray-50 to-white flex-1">
                        {clientData && (
                            <div className="bg-gradient-to-r from-white to-blue-50 rounded-xl border border-blue-100 p-4 shadow-sm">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                                        <FiUser className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800">
                                            {clientData.firstname} {clientData.lastname}
                                        </h3>
                                        <p className="text-sm text-gray-600 flex items-center gap-1">
                                            <FiPhone className="w-3 h-3" />
                                            {clientData.phone}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                                    <p className="mt-4 text-gray-600">Loading boarding history...</p>
                                </div>
                            </div>
                        ) : bookings.length === 0 ? (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <MdHistory className="w-8 h-8 text-gray-400" />
                                </div>
                                <p className="text-gray-600 font-medium">No boarding history found</p>
                                <p className="text-sm text-gray-500 mt-1">This client hasn't made any boarding bookings yet.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {bookings.map((booking, index) => (
                                    <div
                                        key={booking.ref_id || booking._id || index}
                                        className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-all duration-200"
                                    >
                                        <div className="p-3 bg-gradient-to-r from-blue-400 to-blue-600 text-white">
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center gap-2 whitespace-nowrap text-xs">
                                                    <div className="bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-lg text-xs font-bold">
                                                        #{booking.order_number}
                                                    </div>
                                                    <div className="text-xs">
                                                        {formatDate(booking.start_date)}
                                                        {booking.end_date ? ` - ${formatDate(booking.end_date)}` : ""}
                                                    </div>
                                                    <div className={`px-2 py-0.5 rounded-lg text-xs font-bold ${booking.payment_status === "paid"
                                                        ? "bg-green-600 text-white"
                                                        : booking.payment_status === "unpaid"
                                                            ? "bg-red-600 text-white"
                                                            : "bg-gray-800 text-white"
                                                        }`}>
                                                        ₹ {Math.round(Number(booking.total_price || booking.total_amount || 0))}
                                                    </div>
                                                    {booking.status && (
                                                        <div className={`px-2 py-0.5 rounded-lg text-[10px] font-medium ${getStatusBadge(booking.status)}`}>
                                                            • {(booking.status || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                                                        </div>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={() => setExpandedBooking(expandedBooking === booking.ref_id ? null : booking.ref_id)}
                                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
                                                >
                                                    {expandedBooking === booking.ref_id ? (
                                                        <FiChevronUp className="w-5 h-5 text-white" />
                                                    ) : (
                                                        <FiChevronDown className="w-5 h-5 text-white" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        {expandedBooking === booking.ref_id && (
                                            <div className="p-4 space-y-4 bg-gray-50">


                                                {/* Only show check-in/check-out for boarding services */}
                                                {(booking.type === 'boarding' || booking.item_type === 'boarding') && (
                                                    <div className="text-sm font-medium text-gray-700">
                                                        Check-in: {formatDate(booking.start_date)}
                                                        {booking.end_date && (
                                                            <>
                                                                {" "}• Check-out: {formatDate(booking.end_date)}
                                                            </>
                                                        )}
                                                    </div>
                                                )}

                                                <div className="bg-white rounded-lg p-4 shadow-sm">
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                            <MdPets className="w-4 h-4 text-orange-600" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className="font-semibold text-gray-900">{booking.pet_name}</span>
                                                                {booking.pet_size && (
                                                                    <>
                                                                        <span className="text-gray-400">•</span>
                                                                        <span className="text-sm text-gray-600 inline-flex items-center gap-1">
                                                                            <FaRuler className="w-3.5 h-3.5 text-gray-400" />
                                                                            {booking.pet_size}
                                                                        </span>
                                                                    </>
                                                                )}
                                                                {booking.pet_breed && (
                                                                    <>
                                                                        <span className="text-gray-400">•</span>
                                                                        <span className="text-sm text-gray-600">{booking.pet_breed}</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-gray-900 font-medium">{booking.service_name || "Kennel Boarding"}</span>
                                                                <span className="text-green-600 font-bold">₹ {Math.round(Number(booking.total_price || booking.price || 0))}</span>
                                                            </div>
                                                            {booking.service_type_name && (
                                                                <div className="text-xs text-gray-500 mt-0.5">{booking.service_type_name}</div>
                                                            )}
                                                            {(booking.duration || booking.start_time) && (
                                                                <div className="text-xs text-gray-500 mt-0.5">
                                                                    {booking.start_time && `${booking.start_time}`}
                                                                    {booking.duration && booking.start_time && " • "}
                                                                    {booking.duration && `${booking.duration}`}
                                                                </div>
                                                            )}
                                                            {/* Only show room info for boarding services */}
                                                            {(booking.type === 'boarding' || booking.item_type === 'boarding') && (
                                                                <p className="text-sm text-gray-500 mt-1">
                                                                    Room: {booking.room_name || booking.roomTypeName || "N/A"}
                                                                    {booking.room_number ? ` - ${booking.room_number}` : ""}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Only show Products & Addons section for product items, not for boarding, grooming, day_care, vet_clinic, dog_walking */}
                                                {(booking.type === 'product' || booking.item_type === 'product') && (
                                                    <div className="bg-white rounded-lg p-4 shadow-sm">
                                                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                            <MdShoppingCart className="w-4 h-4" />
                                                            Products
                                                        </h4>

                                                        <div className="space-y-2">
                                                            <div className="flex justify-between items-center text-sm">
                                                                <span className="text-gray-700 font-medium">{booking.service_name}</span>
                                                                <span className="text-green-600 font-medium">₹ {Math.round(Number(booking.total_price || 0))}</span>
                                                            </div>
                                                            <div className="flex justify-between items-center text-xs text-gray-500">
                                                                <span>Quantity: {booking.quantity || 1} × ₹ {Math.round(Number(booking.unit_price || 0))} </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}


                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <AddAppointment
                isOpen={showAddAppointment}
                onClose={handleAddAppointmentClose}
                selectedSlotInfo={null}
                clearSlotSelection={() => { }}
                editMode={false}
                editingAppointment={selectedBookingForReorder}
                onSave={handleAddAppointmentSave}
                appointmentStatusRef={null}
            />
        </div>
    );
};

export default BoardingHistoryDrawer;


